import React, { useState } from "react";
import { motion, useInView } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import useAutoCycle from "@/hooks/useAutoCycle";

const list = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.08, delayChildren: 0.06 },
  },
};

const item = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function AnimatedFeatureGrid({ features, testIdPrefix = "patient-feature" }) {
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(null);
  const rootRef = React.useRef(null);
  const inView = useInView(rootRef, { amount: 0.2 });
  const { index, setIndex, progress, reduceMotion } = useAutoCycle(
    features.length,
    4200,
    paused || !inView || hovered != null
  );
  const active = hovered != null ? hovered : index;

  return (
    <motion.div
      ref={rootRef}
      className="features-grid eh-feature-grid"
      variants={list}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
      onMouseLeave={() => {
        setPaused(false);
        setHovered(null);
      }}
    >
      {features.map((feature, i) => {
        const isActive = i === active;
        return (
          <motion.div
            key={feature.title}
            variants={item}
            onMouseEnter={() => {
              setPaused(true);
              setHovered(i);
              setIndex(i);
            }}
            animate={
              reduceMotion
                ? undefined
                : {
                    y: isActive ? -8 : 0,
                    scale: isActive ? 1.015 : 1,
                  }
            }
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <Card
              className={`feature-card eh-feature-card ${i % 2 ? "is-alt" : ""} ${isActive ? "is-active" : ""}`}
              data-testid={`${testIdPrefix}-${i}`}
            >
              <CardContent className="feature-card-content">
                <motion.div
                  className="feature-icon"
                  animate={
                    reduceMotion
                      ? undefined
                      : isActive
                        ? { rotate: -8, scale: 1.08 }
                        : { rotate: 0, scale: 1 }
                  }
                  transition={{ type: "spring", stiffness: 320, damping: 18 }}
                >
                  {feature.icon}
                </motion.div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </CardContent>
              <span className="eh-feature-progress" aria-hidden="true">
                <motion.span
                  style={{ originX: 0 }}
                  animate={{
                    scaleX: isActive && hovered == null ? progress : isActive ? 1 : 0,
                  }}
                  transition={
                    isActive && hovered == null
                      ? { duration: 0.08, ease: "linear" }
                      : { duration: 0.4, ease: [0.22, 1, 0.36, 1] }
                  }
                />
              </span>
            </Card>
          </motion.div>
        );
      })}
    </motion.div>
  );
}
